import { ReactNode, useCallback, useEffect, useState } from 'react';
import { useQuery } from 'react-query';
import { QueryKey } from '@/lib/query-client';
import { UserSettingsModel } from '@/lib/models/user-settings/user-settings.model';
import { getUserSettings, updateUserSettings } from '@/lib/services/user-setting.service';
import useApp from '@/hooks/use-app.hook';
import useToast from '@/hooks/use-toast.hook';
import { LoadState } from '@/lib/helpers';
import { UserSettingsContext } from './helpers';

export const UserSettingsProvider = ({ children }: { children: ReactNode | ReactNode[] }) => {
  const { signedIn } = useApp();
  const { errorToast } = useToast();

  const [settings, setSettings] = useState(new UserSettingsModel());
  const [loadState, setLoadState] = useState<LoadState>('unloaded');

  const { data: rawSettings } = useQuery([QueryKey.UserSettings], () => getUserSettings(), {
    enabled: signedIn,
  });

  const updateUserSettingsWithToast = useCallback(
    (next: UserSettingsModel) => {
      updateUserSettings(next).then(({ updated }) => {
        if (!updated) {
          errorToast('Saving user settings failed. Please try again in a few moments.');
        }
      });
    },
    [errorToast]
  );

  useEffect(() => {
    if (!rawSettings || loadState === 'loaded') {
      return;
    }
    setLoadState('loaded');
    setSettings(rawSettings);
  }, [rawSettings, loadState]);

  const { sidebarOpen } = settings.admin.preferences;

  const handleSidebarToggle = useCallback(() => {
    setSettings((old) => {
      const updated = new UserSettingsModel(old._props);
      updated.admin.preferences.sidebarOpen = !updated.admin.preferences.sidebarOpen;
      updateUserSettingsWithToast(updated);
      return updated;
    });
  }, [updateUserSettingsWithToast]);

  return (
    <UserSettingsContext.Provider
      value={{
        sidebarOpen,
        toggleSidebar: handleSidebarToggle,
        isLoading: loadState !== 'loaded',
      }}
    >
      {children}
    </UserSettingsContext.Provider>
  );
};
